import {
  type MutableRefObject,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { PortalContainerContext } from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';

import styles from './FilterEditorPopover.module.css';

type FilterEditorPopoverProps = {
  children: ReactNode;
  label: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  trigger: (triggerRef: MutableRefObject<HTMLElement | null>) => ReactNode;
};

export const FilterEditorPopover = ({
  children,
  label,
  isOpen,
  onOpenChange,
  trigger,
}: FilterEditorPopoverProps) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const inheritedPortalContainer = useContext(PortalContainerContext);
  const [outerPortalContainer, setOuterPortalContainer] = useState<
    Element | undefined
  >(inheritedPortalContainer);

  useLayoutEffect(() => {
    setOuterPortalContainer(
      inheritedPortalContainer ??
        triggerRef.current?.closest('[role="dialog"]') ??
        undefined,
    );
  }, [inheritedPortalContainer, isOpen]);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null,
  );

  return (
    <>
      {trigger(triggerRef)}
      <PortalContainerContext.Provider value={outerPortalContainer}>
        <Popover
          ref={setPortalContainer}
          className={styles.root}
          aria-label={label}
          hasNoArrow
          isOpen={isOpen}
          placement="bottomStart"
          triggerRef={triggerRef}
          width="max-content"
          onOpenChange={onOpenChange}
        >
          <PortalContainerContext.Provider value={portalContainer ?? undefined}>
            {children}
          </PortalContainerContext.Provider>
        </Popover>
      </PortalContainerContext.Provider>
    </>
  );
};
